import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, Clock, CheckCircle2, XCircle } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { cacheGet, cacheSet, cacheKey } from "../../lib/cache";
import BackButton from "../../components/BackButton";
import { Spinner, formatRupiah } from "../../components/ui";

const STATUS_FILTERS = ["Semua", "Menunggu", "Diterima", "Berlangsung", "Selesai", "Dibatalkan"];

const STATUS_MAP = {
  PENDING_WORKER: { label: "Menunggu Pekerja", color: "bg-amber-50 text-amber-600", icon: Clock },
  ACCEPTED: { label: "Diterima", color: "bg-blue-50 text-blue-600", icon: CheckCircle2 },
  ON_PROGRESS: { label: "Berlangsung", color: "bg-brand-50 text-brand-600", icon: Clock },
  COMPLETED: { label: "Selesai", color: "bg-success-50 text-success-600", icon: CheckCircle2 },
  CONFIRMED: { label: "Dikonfirmasi", color: "bg-success-50 text-success-600", icon: CheckCircle2 },
  REJECTED: { label: "Ditolak", color: "bg-red-50 text-red-600", icon: XCircle },
  CANCELLED: { label: "Dibatalkan", color: "bg-gray-50 text-gray-600", icon: XCircle },
  DISPUTED: { label: "Disputa", color: "bg-red-50 text-red-600", icon: XCircle },
};

const FILTER_STATUS = {
  Semua: null,
  Menunggu: ["PENDING_WORKER"],
  Diterima: ["ACCEPTED"],
  Berlangsung: ["ON_PROGRESS"],
  Selesai: ["COMPLETED", "CONFIRMED"],
  Dibatalkan: ["CANCELLED", "REJECTED"],
};

export default function CustomerBookings() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState(() => cacheGet(cacheKey(user?.id, "cust-bookings")).data || []);
  const [loading, setLoading] = useState(() => bookings.length === 0);
  const [filter, setFilter] = useState("Semua");

  useEffect(() => {
    const key = cacheKey(user?.id, "cust-bookings");
    const fetchBookings = async () => {
      try {
        const { data } = await api.get("/api/bookings");
        setBookings(data);
        cacheSet(key, data);
      } catch (err) {
        console.error("Failed to load bookings:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchBookings();
  }, [user?.id]);

  if (loading) return <Spinner />;

  const filtered = bookings.filter((b) => {
    const allowed = FILTER_STATUS[filter];
    return !allowed || allowed.includes(b.status);
  });

  return (
    <div className="animate-fade-up grid place-items-center py-8">
      <div className="container space-y-6">
        <BackButton to="/customer" />
        <div>
          <h1 className="text-brand-800">Pesanan Saya</h1>
          <p className="text-brand-400 mt-1">Kelola dan pantau semua pesanan Anda</p>
        </div>

        {/* Filter chips */}
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`chip whitespace-nowrap transition ${
                filter === f
                  ? "bg-brand-600 text-white"
                  : "bg-brand-50 text-brand-600 hover:bg-brand-100"
              }`}
              data-testid={`filter-${f.toLowerCase()}`}
            >
              {f}
            </button>
          ))}
        </div>

        {/* Booking list */}
        {filtered.length === 0 ? (
          <div className="card p-10 text-center">
            <ClipboardList className="h-12 w-12 text-brand-200 mx-auto" />
            <p className="font-bold text-brand-800 mt-4">Belum ada pesanan</p>
            <p className="text-brand-400 mt-1">Mulai cari layanan untuk membuat pesanan pertama Anda.</p>
            <Link
              to="/customer/services"
              className="btn-primary mt-6"
              data-testid="browse-services-cta"
            >
              Cari Layanan
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((b) => (
              <BookingCard key={b.id} booking={b} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function BookingCard({ booking }) {
  const status = STATUS_MAP[booking.status] || STATUS_MAP.PENDING_WORKER;
  const StatusIcon = status.icon;

  const formatDate = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    return d.toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" });
  };

  return (
    <Link
      to={`/customer/bookings/${booking.id}`}
      className="card p-4 hover:border-brand-200 transition block"
      data-testid={`booking-card-${booking.id}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="font-bold text-brand-800">{booking.service?.name || "Layanan"}</p>
          <p className="text-sm text-brand-500 mt-1">dengan {booking.worker?.display_name || "Pekerja"}</p>
          <p className="text-sm text-brand-400 mt-0.5">{formatDate(booking.scheduled_at || booking.created_at)}</p>
        </div>
        <span className={`chip ${status.color}`}>
          <StatusIcon className="h-4 w-4" /> {status.label}
        </span>
      </div>
      <div className="mt-3 flex items-center justify-between border-t border-brand-50 pt-3">
        <span className="font-extrabold text-brand-800">{formatRupiah(booking.quoted_amount)}</span>
        <span className="btn-ghost !py-2 !px-4 text-sm" data-testid={`view-booking-${booking.id}`}>
          Lihat Detail
        </span>
      </div>
    </Link>
  );
}
